// The Quantum Transport plots, drawn like the desktop's (qdtransport.plots): energy levels vs field with the
// gap / threshold-field illustrations and parameter box, and the |dI/dV| map on a log colour scale (magma).
import { Badge, Group, SimpleGrid, Switch, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { Plot } from "./Plot";

type Arrays = Record<string, any>;
interface Illustration {
  gaps: { b: number; e_lo: number; e_hi: number; text: string }[];
  markers: { b: number; e: number; text: string }[];
  info: string[];
}

const RED = "crimson";
export const PLOT_HEIGHT = 430;                       // the transport plots' height without the parameter box
const AREA = PLOT_HEIGHT - 45 - 60;                   // the axes' height in px (top and bottom margins)
const LINE = 14;                                      // px per line of the parameter box (font 11)
const MAGMA: [number, string][] = [
  [0, "#000004"], [0.111, "#180f3d"], [0.222, "#440f76"], [0.333, "#721f81"], [0.444, "#9e2f7f"],
  [0.556, "#cd4071"], [0.667, "#f1605d"], [0.778, "#fd9668"], [0.889, "#feca8d"], [1, "#fcfdbf"],
];

function extent(values: Iterable<number>): [number, number] {
  let lo = Infinity, hi = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return lo <= hi ? [lo, hi] : [0, 1];
}

function transpose(m: number[][]): number[][] {
  return m.length ? m[0].map((_, j) => m.map((row) => row[j])) : [];
}

export function levelsFigure(b: number[], levels: number[][], nElectrons: number, axis: "perp" | "par",
                             ill: Illustration | undefined) {
  const cols = transpose(levels);
  const traces: any[] = cols.map((y, i) => ({ x: b, y, type: "scatter", mode: "lines", name: `Level ${i + 1}`, line: { width: 2 } }));
  let [lo, hi] = extent(levels.flat());
  const pad = (hi - lo || 1) * 0.06;
  lo -= pad;
  hi += pad;
  const xr = extent(b);
  const dx = xr[1] - xr[0] || 1;
  const annotations: any[] = [];
  const shapes: any[] = [];
  if (ill) {
    for (const m of ill.markers) {
      if (m.b < xr[0] || m.b > xr[1]) continue;
      shapes.push({ type: "line", x0: m.b, x1: m.b, y0: lo, y1: m.e, line: { color: RED, dash: "dash", width: 1.1 } });
      annotations.push({ x: m.b - 0.01 * dx, y: lo, text: m.text, textangle: -90, xanchor: "right", yanchor: "bottom",
        showarrow: false, font: { color: RED, size: 11 }, bgcolor: "rgba(255,255,255,0.7)" });
    }
    for (const g of ill.gaps) {
      if (g.b < xr[0] || g.b > xr[1]) continue;
      annotations.push({ x: g.b, y: g.e_hi, ax: g.b, ay: g.e_lo, axref: "x", ayref: "y", text: "", showarrow: true,
        arrowside: "end+start", arrowhead: 2, arrowsize: 1, arrowwidth: 1.5, arrowcolor: RED, standoff: 0, startstandoff: 0 });
      annotations.push({ x: g.b + 0.012 * dx, y: (g.e_lo + g.e_hi) / 2, text: g.text, xanchor: "left", showarrow: false,
        font: { color: RED, size: 11 }, bgcolor: "rgba(255,255,255,0.7)" });
    }
  }
  // with the parameter box: legend in one row under the x-axis title, the box under the legend (the figure grows
  // downwards so the axes keep their size); without it: the legend inside the axes
  const info = ill?.info.length ? ill.info : null;
  let extra = 0;
  if (info) {
    const legendTop = 58, boxTop = legendTop + 30;                 // px below the axes
    extra = boxTop + info.length * LINE + 16;
    annotations.push({ xref: "paper", yref: "paper", x: 0, y: -boxTop / AREA, xanchor: "left", yanchor: "top", align: "left",
      showarrow: false, text: info.map((l) => (l === "Parameters" || l === "Derived" ? `<b>${l}</b>` : l)).join("<br>"),
      bgcolor: "whitesmoke", bordercolor: "#999", borderpad: 6, font: { size: 11, color: "#222" } });
  }
  const layout = {
    title: { text: `Energy Spectrum vs Magnetic Field (n=${nElectrons} electrons)`, font: { size: 14 } },
    xaxis: { title: { text: axis === "perp" ? "B<sub>⊥</sub> (T)" : "B<sub>∥</sub> (T)" }, range: xr, gridcolor: "#e6e6e6", griddash: "dash" },
    yaxis: { title: { text: "Eigen Energies (µeV)" }, range: [lo, hi], gridcolor: "#e6e6e6", griddash: "dash" },
    legend: info ? { orientation: "h", x: 0, xanchor: "left", y: -58 / AREA, yanchor: "top" }
      : { x: 0.01, y: 0.99, bgcolor: "rgba(255,255,255,0.7)" },
    shapes, annotations,
    margin: { l: 70, r: 20, t: 45, b: 60 + extra },
    height: PLOT_HEIGHT + extra,
  };
  return { traces, layout };
}

export function didvFigure(z: number[][], vg: number[], vsd: number[], step: [number, number] | undefined, bz: number, preview: boolean) {
  const [sy, sx] = step ?? [1, 1];
  const x = vg.filter((_, i) => i % sx === 0);
  const y = vsd.filter((_, i) => i % sy === 0);
  const logz = z.map((row) => row.map((v) => Math.log10(Math.abs(v) + 1e-20)));
  const [zmin, zmax] = extent(logz.flat());
  const ticks: number[] = [];
  for (let k = Math.ceil(zmin - 1e-3); k <= Math.floor(zmax + 1e-3); k++) ticks.push(k);
  return {
    traces: [{ z: logz, x, y, type: "heatmap", colorscale: MAGMA, zmin, zmax: zmax > zmin ? zmax : zmin + 1,
      colorbar: { title: { text: "|dI/dV|" }, tickvals: ticks, ticktext: ticks.map((k) => `1e${k}`) },
      customdata: z, hovertemplate: "V<sub>g</sub> %{x:.4g} µeV<br>V<sub>sd</sub> %{y:.4g} µeV<br>|dI/dV| %{customdata:.3e}<extra></extra>" }],
    layout: {
      title: { text: `B<sub>⊥</sub> = ${bz.toFixed(2)} T${preview ? " (quick preview)" : ""}`, font: { size: 14 } },
      xaxis: { title: { text: "Gate Voltage - V<sub>g</sub> (µeV)" } },
      yaxis: { title: { text: "Bias Voltage - V<sub>sd</sub> (µeV)" } },
    },
  };
}

export function TransportPlots({ result }: { result: Record<string, any> }) {
  const [illustrate, setIllustrate] = useState(true);
  const a: Arrays = result.arrays ?? {};
  const ill = illustrate ? result.illustrations : null;
  const mode: string = result.mode;

  const perp = useMemo(() => (a.b_values && a.perp ? levelsFigure(a.b_values, a.perp, result.n_electrons, "perp", ill?.perp) : null),
    [a.b_values, a.perp, result.n_electrons, ill]);
  const par = useMemo(() => (mode === "energy" && a.b_values && a.par ? levelsFigure(a.b_values, a.par, result.n_electrons, "par", ill?.par) : null),
    [mode, a.b_values, a.par, result.n_electrons, ill]);
  const map = useMemo(() => {
    const z = a.didv ?? a.didv_preview;
    return mode === "transport" && z && a.vg && a.vsd
      ? didvFigure(z, a.vg, a.vsd, a.didv_preview_step, Number(result.bz_plot ?? 0), !!result.preview) : null;
  }, [mode, a.didv, a.didv_preview, a.didv_preview_step, a.vg, a.vsd, result.bz_plot, result.preview]);

  return (
    <>
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <Text size="sm" c="dimmed">{result.n_electrons} electron(s)</Text>
          {result.preview && <Badge color="orange" variant="light">Quick preview - the full map is still running</Badge>}
          {a.didv_preview && !result.preview && <Badge variant="light">Map shown at reduced resolution; the download has every point</Badge>}
        </Group>
        {result.illustrations && (
          <Switch size="xs" label="Illustrate plots (gaps, threshold fields)" checked={illustrate}
            onChange={(e) => setIllustrate(e.currentTarget.checked)} />
        )}
      </Group>
      <SimpleGrid cols={{ base: 1, xl: 2 }}>
        {mode === "transport" && map && <Plot data={map.traces} layout={map.layout} height={PLOT_HEIGHT} />}
        {perp && <Plot data={perp.traces} layout={perp.layout} height={PLOT_HEIGHT} />}
        {par && <Plot data={par.traces} layout={par.layout} height={PLOT_HEIGHT} />}
      </SimpleGrid>
    </>
  );
}
